/**
 * modelSettings — the app's side of what each portfolio saves for its model
 * portfolio (WORK.md item 57): a re-export of the cross-runtime module at
 * lib/modelSettings.ts (the shape, the bounds, the residence presets, the
 * reader and the edge's write guard) and the zod schema of one stored block.
 *
 * The schema is built from that module's bounds, never a copy of them, and
 * `modelSettingsSchema.test.ts` holds it to the edge's guard case by case, so
 * the app cannot accept a block the edge refuses. `SettingsSchema` keeps the
 * two layers loose (`z.unknown()`) so one bad field never fails the parse of
 * the whole settings blob: `resolveModelSettings` leaves it out and says so.
 */
import { z } from 'zod';
import {
  AIM_KINDS, INSTRUMENT_TYPES, REVIEW_FREQUENCIES, MAX_AVAILABLE_FUNDS, MAX_HELD_LIST, MAX_UNIVERSE_LIST, MODEL_BOUNDS,
  MODEL_TICKER_MAX_LEN, findPreset, type Bound,
} from '../../../lib/modelSettings';

export * from '../../../lib/modelSettings';

function bounded(b: Bound, opts: { integer?: boolean } = {}) {
  let n = z.number().finite();
  if (opts.integer) n = n.int();
  n = b.minExclusive ? n.gt(b.min) : n.gte(b.min);
  return n.lte(b.max);
}

const fraction = (b: Bound) => bounded(b);
const nullableFraction = (b: Bound) => bounded(b).nullable();

function uniqueList<T extends z.ZodTypeAny>(item: T, max: number, min = 0) {
  return z.array(item).min(min).max(max).refine((xs) => new Set(xs).size === xs.length, 'names an entry twice');
}

const ticker = z.string().min(1).max(MODEL_TICKER_MAX_LEN).refine((t) => t.trim() !== '', 'is blank');
const alpha2 = z.string().regex(/^[A-Z]{2}$/);
const alpha3 = z.string().regex(/^[A-Z]{3}$/);
const exchange = z.string().regex(/^[A-Z0-9]{1,12}$/);
const anyOrList = <T extends z.ZodTypeAny>(item: T, max: number) => uniqueList(item, max, 1).nullable();

export const ModelRefSchema = z.object({
  id: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/),
  version: bounded(MODEL_BOUNDS.modelVersion, { integer: true }),
}).strict();

export const AimSchema = z.object({
  kind: z.enum(AIM_KINDS),
  value: z.number().nullable().optional(),
}).strict().superRefine((a, ctx) => {
  const value = a.value ?? null;
  if (a.kind === 'target_vol' || a.kind === 'target_return') {
    const b = a.kind === 'target_vol' ? MODEL_BOUNDS.targetVol : MODEL_BOUNDS.targetReturn;
    if (value === null || !bounded(b).safeParse(value).success) {
      ctx.addIssue({ code: 'custom', path: ['value'], message: `out of bounds for ${a.kind}` });
    }
  } else if (value !== null) {
    ctx.addIssue({ code: 'custom', path: ['value'], message: `only for target_vol and target_return` });
  }
});

export const UniverseFilterSchema = z.object({
  preset: z.object({ id: z.string(), version: z.number() }).strict().nullable()
    .refine((p) => p === null || findPreset(p.id, p.version) !== null, 'names no preset'),
  countries: anyOrList(alpha2, MAX_UNIVERSE_LIST),
  exchanges: anyOrList(exchange, MAX_UNIVERSE_LIST),
  domiciles: anyOrList(alpha2, MAX_UNIVERSE_LIST),
  fundDomicilesExcluded: uniqueList(alpha2, MAX_UNIVERSE_LIST),
  types: anyOrList(z.enum(INSTRUMENT_TYPES), INSTRUMENT_TYPES.length),
  currencies: anyOrList(alpha3, MAX_UNIVERSE_LIST),
  ucits: z.enum(['any', 'required']),
  availableFunds: anyOrList(ticker, MAX_AVAILABLE_FUNDS),
}).strict();

/** One stored block, the person's default or one portfolio's own: every field optional. */
export const ModelSettingsBlockSchema = z.object({
  model: ModelRefSchema.nullable(),
  aim: AimSchema,
  limits: z.object({
    volCap: nullableFraction(MODEL_BOUNDS.volCap),
    returnFloor: nullableFraction(MODEL_BOUNDS.returnFloor),
    maxDrawdown: nullableFraction(MODEL_BOUNDS.maxDrawdown),
    maxTurnover: nullableFraction(MODEL_BOUNDS.maxTurnover),
  }).strict().partial(),
  frozen: uniqueList(ticker, MAX_HELD_LIST),
  noSell: uniqueList(ticker, MAX_HELD_LIST),
  newHoldings: z.boolean(),
  suggestions: z.object({ stocks: z.boolean(), funds: z.boolean(), alpha: z.boolean() }).strict().partial(),
  crypto: z.object({
    allowed: z.boolean(),
    expectedReturn: nullableFraction(MODEL_BOUNDS.cryptoExpectedReturn),
  }).strict().partial(),
  groupMax: z.object({
    equity: fraction(MODEL_BOUNDS.groupMax),
    bonds: fraction(MODEL_BOUNDS.groupMax),
  }).strict().partial(),
  costs: z.object({
    dealingCharge: bounded(MODEL_BOUNDS.dealingCharge),
    fxFee: fraction(MODEL_BOUNDS.fxFee),
    platformFee: fraction(MODEL_BOUNDS.platformFee),
  }).strict().partial(),
  triggers: z.object({
    returnGap: fraction(MODEL_BOUNDS.returnGap),
    confidence: fraction(MODEL_BOUNDS.confidence),
    volGap: fraction(MODEL_BOUNDS.volGap),
    minNetGain: fraction(MODEL_BOUNDS.minNetGain),
    cooldownDays: bounded(MODEL_BOUNDS.cooldownDays, { integer: true }),
    aeaFlag: z.boolean(),
  }).strict().partial(),
  universe: UniverseFilterSchema.nullable(),
  review: z.object({
    frequency: z.enum(REVIEW_FREQUENCIES),
    day: bounded(MODEL_BOUNDS.reviewDay, { integer: true }).nullable(),
  }).strict().partial(),
}).strict().partial().superRefine((b, ctx) => {
  const frozen = new Set(b.frozen ?? []);
  const both = (b.noSell ?? []).find((t) => frozen.has(t));
  if (both !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['noSell'], message: `${both} is frozen` });
  }
  if (b.review?.frequency === 'weekly' && b.review.day != null && b.review.day > 7) {
    ctx.addIssue({ code: 'custom', path: ['review', 'day'], message: 'must be 1-7 for a weekly review' });
  }
});
export type ModelSettingsBlock = z.infer<typeof ModelSettingsBlockSchema>;
